import { createGetRequest } from 'app/selectors';
import { ActKc2StatusEnum, ContractStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { Position } from 'uikit';

import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ModalWorkOrderEnum } from '../workOrderConstants';
import { WorkOrderChapters } from '../workorderTypes';

enum TooltipComponentName {
    ActKC3 = 'ActKC3',
    ActKC2 = 'ActKC2',
    Other = 'Other',
    Uer = 'Uer',
}

export enum ModalName {
    Other = 'Other',
    AddActKC2 = 'AddActKC2',
    // AddOrEditActKc3 = 'AddOrEditActKc3',
    Uer = 'Uer',
    Coefficient = 'Coefficient',
}

interface IUseAddToWorkOrderMenu {
    isAddOtherBtnDisabled: boolean;
    isDisabledByProjectStatus: boolean;
    filters: { objectId: number; subObjectId: number; chapter: WorkOrderChapters };
    // isSomeActKC2Accepted: boolean;
    isHaveTooltipOther: boolean;
    statusContract?: ContractStatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
    isNotActKC2: boolean;
    isAllActKC2Accepted: boolean;
    openModal: (modalType: ModalWorkOrderEnum) => void;
}

export const useAddToWorkOrderMenu = (props: IUseAddToWorkOrderMenu) => {
    const {
        isDisabledByProjectStatus,
        isAddOtherBtnDisabled,
        statusWorkOrder,
        filters,
        statusContract,
        // isSomeActKC2Accepted,
        isHaveTooltipOther,
        isNotActKC2,
        isAllActKC2Accepted,
        openModal,
    } = props;

    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const requestSelector = useMemo(() => createGetRequest('workOrderPriceListActs'), []);
    const { data: workOrderPriceListActs } = useSelector(requestSelector) ?? {};

    const [openedModal, setOpenedModal] = useState<{ [key: string]: boolean }>({});

    const openModalByName = useCallback((modalName) => {
        setOpenedModal({ [modalName]: true });
    }, []);

    const closeModalsHandler = useCallback(() => {
        setOpenedModal({});
    }, []);

    const { aktKC2: aktKC2Rules } = useAccess();

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const isStatusWorkOrderOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;
    const isStatusWorkOrderInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    // const isStatusWorkOrderDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;
    // const isStatusWorkOrderCompleted = statusWorkOrder === WorkOrderStatusEnum.Completed;

    const isCanCreateAktKC2 = useMemo(() => aktKC2Rules.canCreate, [aktKC2Rules.canCreate]);
    const isOpenTooltipActKC2 =
        !isCanCreateAktKC2 ||
        isStatusWorkOrderOnFormation ||
        (isStatusWorkOrderInProgress && !(filters.chapter && filters.objectId));

    const isAddOthersDisabled = useMemo(
        () => isAddOtherBtnDisabled || isDisabledByProjectStatus,
        [isAddOtherBtnDisabled, isDisabledByProjectStatus],
    );

    const isAddActKC2Disabled = useMemo(
        () =>
            !isStatusWorkOrderInProgress ||
            filters.chapter === WorkOrderChapters.SUMMARY ||
            !(filters.chapter && filters.objectId) ||
            !isContractStatusWork ||
            !isCanCreateAktKC2 ||
            isDisabledByProjectStatus,
        [
            filters.chapter,
            filters.objectId,
            isCanCreateAktKC2,
            isContractStatusWork,
            isDisabledByProjectStatus,
            isStatusWorkOrderInProgress,
        ],
    );

    const isNotPriceListActs = useMemo(() => {
        const workOrderPriceListActsItems = workOrderPriceListActs?.uerWorkOrderActsKC2?.items ?? [];

        const isActsWithValidStatus = !workOrderPriceListActsItems?.filter(
            (itemAct: { status: ActKc2StatusEnum }) =>
                itemAct.status === ActKc2StatusEnum.InProgress || itemAct.status === ActKc2StatusEnum.OnCheck,
        )?.length;

        return isActsWithValidStatus;
    }, [workOrderPriceListActs]);

    const isNotActKC2WorkOrderInProgress = useMemo(
        () => isStatusWorkOrderInProgress && (isNotActKC2 || isAllActKC2Accepted || isNotPriceListActs),
        [isAllActKC2Accepted, isNotActKC2, isNotPriceListActs, isStatusWorkOrderInProgress],
    );

    const isAddUerDisabled = useMemo(
        () =>
            !filters.subObjectId ||
            !filters.chapter ||
            filters.chapter === WorkOrderChapters.SUMMARY ||
            filters.chapter === WorkOrderChapters.OTHERS ||
            !isContractStatusWork ||
            statusWorkOrder === WorkOrderStatusEnum.Delayed ||
            isNotActKC2WorkOrderInProgress ||
            isDisabledByProjectStatus,
        [
            filters.chapter,
            filters.subObjectId,
            isContractStatusWork,
            isDisabledByProjectStatus,
            isNotActKC2WorkOrderInProgress,
            statusWorkOrder,
        ],
    );

    const isDisabledCoefficient = useMemo(
        () => statusWorkOrder !== WorkOrderStatusEnum.OnFormation || !filters.subObjectId || !filters.chapter,
        [filters.chapter, filters.subObjectId, statusWorkOrder],
    );

    // const isActKC3ModalDisabled = useMemo(() => !isSomeActKC2Accepted, [isSomeActKC2Accepted]);

    // const isKC3Disabled = useMemo(
    //     () =>
    //         !isStatusWorkOrderInProgress ||
    //         isDisabledByProjectStatus ||
    //         !isContractStatusWork ||
    //         isStatusWorkOrderDelayed ||
    //         isStatusWorkOrderCompleted ||
    //         isNotActKC2,
    //     [
    //         isContractStatusWork,
    //         isDisabledByProjectStatus,
    //         isStatusWorkOrderCompleted,
    //         isStatusWorkOrderDelayed,
    //         isStatusWorkOrderInProgress,
    //         isNotActKC2,
    //     ],
    // );

    const analyticsData = useMemo(
        () => ({
            projectId,
            contractId: projectData?.contractId,
            orderId: Number(orderId),
            orderStatus: statusWorkOrder,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            orderId,
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            statusWorkOrder,
        ],
    );

    const menuItems = useMemo(
        () => [
            {
                label: 'Расценку',
                action: () => {
                    openModalByName(ModalName.Uer);

                    if (statusWorkOrder === WorkOrderStatusEnum.OnFormation) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_NZ_add price button_pressed',
                            ...analyticsData,
                        });
                    } else if (statusWorkOrder === WorkOrderStatusEnum.InProgress) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_act KC_add price button_pressed',
                            ...analyticsData,
                        });
                    }
                },
                disabled: isAddUerDisabled,
                haveTooltip: isAddUerDisabled,
                tooltipName: TooltipComponentName.Uer,
            },
            {
                label: 'Коэффициент',
                action: () => {
                    openModal(ModalWorkOrderEnum.WorkOrderUseCoefficient);
                },
                disabled: isDisabledCoefficient,
                haveTooltip: isAddUerDisabled,
                tooltipName: TooltipComponentName.Uer,
            },
            {
                label: 'Прочие',
                action: () => {
                    openModalByName(ModalName.Other);

                    if (isStatusWorkOrderOnFormation) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_NZ_add other button_pressed',
                            ...analyticsData,
                        });
                    }

                    if (isStatusWorkOrderInProgress) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_act KC_add other button_pressed',
                            ...analyticsData,
                        });
                    }
                },
                disabled: isAddOthersDisabled,
                haveTooltip: isHaveTooltipOther,
                tooltipName: TooltipComponentName.Other,
            },
            {
                label: 'Акт КС-2',
                action: () => {
                    openModalByName(ModalName.AddActKC2);

                    sendGpnAnalyticsEvent({
                        componentId: 'rim_add act KC button_pressed',
                        ...analyticsData,
                    });
                },
                disabled: isAddActKC2Disabled,
                haveTooltip: isOpenTooltipActKC2,
                tooltipName: TooltipComponentName.ActKC2,
            },
            // {
            //     label: 'Справку КС-3',
            //     action: () => {
            //         openModalByName(ModalName.AddOrEditActKc3);
            //
            //         sendGpnAnalyticsEvent({
            //             componentId: 'rim_add KS TRI button_pressed',
            //             ...analyticsData,
            //         });
            //     },
            //     disabled: isKC3Disabled,
            //     haveTooltip: isActKC3ModalDisabled || !isStatusWorkOrderInProgress,
            //     tooltipName: TooltipComponentName.ActKC3,
            // },
        ],
        [
            analyticsData,
            isAddActKC2Disabled,
            isAddOthersDisabled,
            isAddUerDisabled,
            isDisabledCoefficient,
            isHaveTooltipOther,
            isOpenTooltipActKC2,
            isStatusWorkOrderInProgress,
            isStatusWorkOrderOnFormation,
            openModal,
            openModalByName,
            sendGpnAnalyticsEvent,
            statusWorkOrder,
        ],
    );

    const getMenuItemAttributes = useCallback((item: { haveTooltip: boolean; tooltipName: string }) => {
        if (item.haveTooltip) {
            if (item.tooltipName === TooltipComponentName.ActKC3) {
                return {
                    readOnly: true,
                    name: TooltipComponentName.ActKC3,
                };
            }
            if (item.tooltipName === TooltipComponentName.ActKC2) {
                return {
                    readOnly: true,
                    name: TooltipComponentName.ActKC2,
                };
            }
            if (item.tooltipName === TooltipComponentName.Other) {
                return {
                    readOnly: true,
                    name: TooltipComponentName.Other,
                };
            }
            if (item.tooltipName === TooltipComponentName.Uer) {
                return {
                    readOnly: true,
                    name: TooltipComponentName.Uer,
                };
            }
        }

        return undefined;
    }, []) as any;

    const [tooltipPosition, setTooltipPosition] = useState<Position>(undefined);
    const [activeTooltip, setActiveTooltip] = useState<{ [key: string]: boolean }>({});

    const onMenuMouseMove = useCallback((event) => {
        const attr = event.target.getAttributeNames();
        const hasTooltip = attr.includes('readonly');
        const target = event.target.getAttribute('name');
        const rect = event.currentTarget.getBoundingClientRect();
        const targetRect = event.target.getBoundingClientRect();

        const heightItemMenu = 24;

        if (hasTooltip) {
            if (target === TooltipComponentName.ActKC3) {
                setActiveTooltip({ [TooltipComponentName.ActKC3]: true });
            }
            if (target === TooltipComponentName.ActKC2) {
                setActiveTooltip({ [TooltipComponentName.ActKC2]: true });
            }
            if (target === TooltipComponentName.Other) {
                setActiveTooltip({ [TooltipComponentName.Other]: true });
            }
            if (target === TooltipComponentName.Uer) {
                setActiveTooltip({ [TooltipComponentName.Uer]: true });
            }

            setTooltipPosition({ x: rect.right, y: targetRect.bottom - heightItemMenu / 2 });
        } else {
            setActiveTooltip({});
        }
    }, []);

    const onMenuMouseLeave = useCallback(() => {
        setActiveTooltip({});
    }, []);

    return useMemo(
        () => ({
            addToWorkOrderMenuItems: menuItems,
            isAddOtherOpened: openedModal[ModalName.Other],
            isAddActKC2Opened: openedModal[ModalName.AddActKC2],
            // isActKC3ModalOpened: openedModal[ModalName.AddOrEditActKc3],
            isCoefficientModalOpened: openedModal[ModalName.Coefficient],
            isUerModalOpened: openedModal[ModalName.Uer],
            closeModalsHandler,
            getMenuItemAttributes,
            onMenuMouseMove,
            tooltipPosition,
            // actKc3TooltipActive: activeTooltip[TooltipComponentName.ActKC3],
            actKc2TooltipActive: activeTooltip[TooltipComponentName.ActKC2],
            actKc2OtherTooltipActive: activeTooltip[TooltipComponentName.Other],
            uerTooltipActive: activeTooltip[TooltipComponentName.Uer],
            onMenuMouseLeave,
        }),
        [
            activeTooltip,
            closeModalsHandler,
            getMenuItemAttributes,
            menuItems,
            onMenuMouseLeave,
            onMenuMouseMove,
            openedModal,
            tooltipPosition,
        ],
    );
};
